import React, { useState } from "react";

// Third Party
import { useMutation } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";

// UI
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogClose } from "@/components/ui/dialog";
import { Badge } from "../ui/badge";
import { Spinner } from "../ui/spinner";
import { useToast } from "../ui/use-toast";

// Icons
import {
    MapPin,
    FileText,
    Play,
    Pause,
    Download,
    Eye,
    X,
    CircleSlash2,
} from "lucide-react";

// Utils and Type
import { cn } from "@/lib/utils";
import {
    CHAT_BUCKET_LINK,
    CHAT_FILE,
    CHAT_MEDIUM,
} from "../constants";
import { acceptDocument } from "@/services/chat";
import { Message } from "@/types/chat";
import useUserToken from "@/hook/useUserToken";


export type MessageType =
    | "TEXT"
    | "IMAGE"
    | "VIDEO"
    | "REACTION"
    | "VOICE"
    | "FILE"
    | "STICKER"
    | "LOCATION"
    | "VOICE_CALL"
    | "VIDEO_CALL"
    | "SYSTEM";

type Props = {
    conversationId: string;
    message: Message
    content: string;
    type: MessageType;
    isCurrentUser?: boolean;
    timestamp?: string;
    senderName?: string;
    showSender?: boolean;
    attachments?: any[];
    location?: {
        lng: number;
        lat: number;
    };
    isClickReceive?: boolean;
    clickReceivedBy?: string[];
    isDeleted: boolean;
};

const RenderMessage: React.FC<Props> = ({
    conversationId,
    message,
    content,
    type,
    isCurrentUser = false,
    timestamp,
    senderName,
    showSender = false,
    attachments,
    location,
    isClickReceive,
    clickReceivedBy,
    participants,
    isDeleted,
}) => {
    const { toast } = useToast();
    const user = useUserToken();
    const queryClient = useQueryClient();

    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const [isPlaying, setIsPlaying] = React.useState(false);
    const audioRef = React.useRef<HTMLAudioElement>(null);
    const videoRef = React.useRef<HTMLVideoElement>(null);

    console.log(message, "Message")

    // Mutation
    const acceptDocumentMutation = useMutation({
        mutationFn: () => acceptDocument({
            userId: user?.data?.userChatId,
            messageId: message?._id
        }),
        onSuccess: (data) => {
            console.log("Success", data);

            queryClient.invalidateQueries({ queryKey: ["messages", conversationId] });


            toast({
                title: "ສຳເລັດ",
                description: "ຮັບເອກະສານສຳເລັດ",
            });
        },
        onError: (error) => {
            console.log("Error", error);

            toast({
                title: "ຫຼົ້ມເຫຼວ",
                description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
                variant: "destructive"
            });
        },
    });

    // Function
    const toggleAudioPlayback = () => {
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
            } else {
                audioRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const handleAudioEnded = () => {
        setIsPlaying(false);
    };

    // Helper function to get file extension
    const getFileExtension = (fileName: string) => {
        return fileName.split(".").pop()?.toLowerCase() || "";
    };

    // Helper function to determine if a file is an image
    const isImageFile = (fileName: string) => {
        const ext = getFileExtension(fileName);
        return ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext);
    };

    // Helper function to determine if a file is a video
    const isVideoFile = (fileName: string) => {
        const ext = getFileExtension(fileName);
        return ["mp4", "webm", "ogg", "mov", "avi"].includes(ext);
    };

    // Helper function to determine if a file is an audio file
    const isAudioFile = (fileName: string) => {
        const ext = getFileExtension(fileName);
        return ["mp3", "wav", "ogg", "aac"].includes(ext);
    };

    // Create a unique audio ref for each audio attachment
    const audioElementRef = React.useRef<HTMLAudioElement>(null);
    const [audioPlaying, setAudioPlaying] = React.useState(false);
    const [audioDuration, setAudioDuration] = React.useState(0);
    const [currentTime, setCurrentTime] = React.useState(0);
    const [audioProgress, setAudioProgress] = React.useState(0);
    const [audioFrequencies] = React.useState<number[]>(() =>
        Array.from({ length: 30 }, () => Math.floor(Math.random() * 90) + 10),
    );

    // Format time in MM:SS format
    const formatTime = (timeInSeconds: number) => {
        const minutes = Math.floor(timeInSeconds / 60);
        const seconds = Math.floor(timeInSeconds % 60);
        return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
    };

    const toggleAudio = () => {
        if (audioElementRef.current) {
            if (audioPlaying) {
                audioElementRef.current.pause();
            } else {
                audioElementRef.current.play();
            }
            setAudioPlaying(!audioPlaying);
        }
    };

    const handleAudioTimeUpdate = () => {
        if (audioElementRef.current) {
            const progress =
                (audioElementRef.current.currentTime /
                    audioElementRef.current.duration) *
                100;
            setAudioProgress(progress);
            setCurrentTime(audioElementRef.current.currentTime);
        }
    };

    const handleAudioEnd = () => {
        setAudioPlaying(false);
        setAudioProgress(0);
        setCurrentTime(0);
    };

    const handleAudioLoaded = () => {
        if (audioElementRef.current) {
            setAudioDuration(audioElementRef.current.duration);
        }
    };

    // Handle seeking when user clicks on the progress bar
    const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
        if (audioElementRef.current) {
            const progressBar = e.currentTarget;
            const rect = progressBar.getBoundingClientRect();
            const clickPosition = e.clientX - rect.left;
            const percentClicked = (clickPosition / rect.width) * 100;
            const seekTime =
                (percentClicked / 100) * audioElementRef.current.duration;

            audioElementRef.current.currentTime = seekTime;
            setAudioProgress(percentClicked);
            setCurrentTime(seekTime);
        }
    };

    const renderAttachments = () => {
        if (!attachments?.length) return null;

        return (
            <div className="mb-2 space-y-2">
                {attachments.map((attachment, index) => {
                    const fileName = attachment.fileName || "";
                    const fileUrl = CHAT_MEDIUM + fileName;

                    // Determine file type based on extension
                    if (isImageFile(fileName)) {
                        return (
                            <div key={index} className="max-w-[300px]">
                                <img
                                    onKeyDown={() => { }}
                                    src={fileUrl}
                                    alt={`Attachment ${index + 1}`}
                                    className="max-w-full rounded-md transition-opacity cursor-pointer hover:opacity-90"
                                    onClick={() => setPreviewImage(CHAT_FILE + fileName)}
                                />
                            </div>
                        );
                    }
                    if (isVideoFile(fileName)) {
                        return (
                            <div key={index} className="max-w-[300px]">
                                <video
                                    src={fileUrl}
                                    controls
                                    className="max-w-full rounded-md"
                                    preload="metadata"
                                />
                            </div>
                        );
                    }
                    if (isAudioFile(fileName)) {
                        return (
                            <div key={index} className="flex flex-col gap-2">
                                <div className="flex gap-2 items-center">
                                    <Button
                                        size="icon"
                                        variant="outline"
                                        onClick={toggleAudio}
                                        className="flex-shrink-0 w-10 h-10 rounded-full"
                                    >
                                        {audioPlaying ? <Pause size={18} /> : <Play size={18} />}
                                    </Button>
                                    <div className="flex-1">
                                        <div
                                            onKeyDown={() => { }}
                                            className="h-[40px] bg-muted rounded-lg relative overflow-hidden cursor-pointer"
                                            onClick={handleSeek}
                                        >
                                            {/* Frequency visualization bars */}
                                            <div className="flex absolute inset-0 justify-between items-center px-2">
                                                {audioFrequencies.map((height, i) => (
                                                    <div
                                                        key={i}
                                                        className={cn(
                                                            "rounded-full transition-all duration-300 w-[2px]",
                                                            isCurrentUser
                                                                ? "bg-primary-foreground/40"
                                                                : "bg-primary/40",
                                                        )}
                                                        style={{
                                                            height: `${height}%`,
                                                            opacity:
                                                                audioProgress >
                                                                    (i / audioFrequencies.length) * 100
                                                                    ? 1
                                                                    : 0.4,
                                                        }}
                                                    />
                                                ))}
                                            </div>

                                            {/* Progress overlay */}
                                            <div
                                                className={cn(
                                                    "absolute top-0 left-0 h-full bg-primary/10",
                                                    isCurrentUser
                                                        ? "bg-primary-foreground/10"
                                                        : "bg-primary/10",
                                                )}
                                                style={{ width: `${audioProgress}%` }}
                                            />

                                            {/* Progress indicator */}
                                            <div
                                                className={cn(
                                                    "absolute top-0 w-1 h-full",
                                                    isCurrentUser
                                                        ? "bg-primary-foreground"
                                                        : "bg-primary",
                                                )}
                                                style={{
                                                    left: `calc(${audioProgress}% - 2px)`,
                                                    display: audioProgress > 0 ? "block" : "none",
                                                }}
                                            />
                                        </div>
                                        <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                                            <span>{formatTime(currentTime)}</span>
                                            <span>
                                                {audioDuration ? formatTime(audioDuration) : "--:--"}
                                            </span>
                                        </div>
                                        <audio
                                            ref={audioElementRef}
                                            src={CHAT_BUCKET_LINK + "/images/" + fileName}
                                            onEnded={handleAudioEnd}
                                            onTimeUpdate={handleAudioTimeUpdate}
                                            onLoadedMetadata={handleAudioLoaded}
                                            className="hidden"
                                        />
                                    </div>
                                </div>
                                <div className="ml-12 text-xs text-muted-foreground">
                                    ຂໍ້ຄວາມສຽງ
                                </div>
                            </div>
                        );
                    }
                    // Default to file display for any other type
                    return (
                        <Card key={index} className="flex gap-2 items-center p-3">
                            <FileText size={24} />
                            <div className="overflow-hidden flex-1">
                                <p className="text-sm font-medium truncate">{fileName}</p>
                            </div>
                            <div>
                                {/* <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => window.open(CHAT_PREVIEW + fileName, "_blank")}
                                >
                                    <Eye size={18} />
                                </Button> */}
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => window.open(CHAT_FILE + fileName, "_blank")}
                                >
                                    <Download size={18} />
                                </Button>
                            </div>
                        </Card>
                    );
                })}
            </div>
        );
    };

    const renderContent = () => {
        // If there are attachments and the message type is FILE, IMAGE, VIDEO, or VOICE,
        // we'll let the renderAttachments function handle the display
        if (
            attachments?.length &&
            ["FILE", "IMAGE", "VIDEO", "VOICE"].includes(type)
        ) {
            return null; // Don't render content separately as it's handled by attachments
        }

        switch (type) {
            case "TEXT": {
                // Function to detect URLs in text and make them clickable
                const renderTextWithLinks = (text: string) => {
                    // Regular expression to match URLs
                    const urlRegex = /(https?:\/\/[^\s]+)/g;

                    if (!text.match(urlRegex)) {
                        // If no URLs in text, return the text as is
                        return text;
                    }

                    // Create an array to hold the result components
                    const result = [];
                    let lastIndex = 0;

                    // Find all matches and their positions
                    let match;
                    while ((match = urlRegex.exec(text)) !== null) {
                        const url = match[0];
                        const matchIndex = match.index;

                        // Add text before the URL
                        if (matchIndex > lastIndex) {
                            result.push(
                                <span key={`text-${lastIndex}`}>
                                    {text.substring(lastIndex, matchIndex)}
                                </span>,
                            );
                        }

                        // Add the URL
                        result.push(
                            <a
                                key={`link-${matchIndex}`}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-500 hover:underline"
                                onClick={(e) => {
                                    e.stopPropagation();
                                }}
                            >
                                {url}
                            </a>,
                        );

                        // Update lastIndex to after this URL
                        lastIndex = matchIndex + url.length;
                    }

                    // Add any remaining text after the last URL
                    if (lastIndex < text.length) {
                        result.push(
                            <span key={`text-${lastIndex}`}>
                                {text.substring(lastIndex)}
                            </span>,
                        );
                    }

                    return result;
                };

                return (
                    <p className="whitespace-pre-wrap break-words">
                        {renderTextWithLinks(content)}
                    </p>
                );
            }

            case "IMAGE":
                return (
                    <div className="">
                        <img
                            onKeyDown={() => { }}
                            src={CHAT_MEDIUM + content}
                            alt="Message image"
                            className="max-w-full rounded-md transition-opacity cursor-pointer hover:opacity-90"
                            // onClick={() => setPreviewImage(CHAT_MEDIUM + content)}
                            onClick={() => setPreviewImage(CHAT_FILE + content)}
                        />
                    </div>
                );

            case "VIDEO":
                return (
                    <div className="">
                        <video
                            ref={videoRef}
                            src={CHAT_MEDIUM + content}
                            controls
                            className="max-w-full rounded-md"
                            preload="metadata"
                        />
                    </div>
                );

            case "VOICE":
                return (
                    <div className="flex flex-col gap-2">
                        <div className="flex gap-2 items-center">
                            <Button
                                size="icon"
                                variant="outline"
                                onClick={toggleAudioPlayback}
                                className="flex-shrink-0 w-10 h-10 rounded-full"
                            >
                                {isPlaying ? <Pause size={18} /> : <Play size={18} />}
                            </Button>
                            <div className="flex-1">
                                <div
                                    onKeyDown={() => { }}
                                    className="h-[40px] bg-muted rounded-lg relative overflow-hidden cursor-pointer"
                                    onClick={handleSeek}
                                >
                                    {/* Frequency visualization bars */}
                                    <div className="flex absolute inset-0 justify-between items-center px-2">
                                        {audioFrequencies.map((height, i) => (
                                            <div
                                                key={i}
                                                className={cn(
                                                    "rounded-full transition-all duration-300 w-[2px]",
                                                    isCurrentUser
                                                        ? "bg-primary-foreground/40"
                                                        : "bg-primary/40",
                                                )}
                                                style={{
                                                    height: `${height}%`,
                                                    opacity:
                                                        audioProgress > (i / audioFrequencies.length) * 100
                                                            ? 1
                                                            : 0.4,
                                                }}
                                            />
                                        ))}
                                    </div>

                                    {/* Progress overlay */}
                                    <div
                                        className={cn(
                                            "absolute top-0 left-0 h-full bg-primary/10",
                                            isCurrentUser
                                                ? "bg-primary-foreground/10"
                                                : "bg-primary/10",
                                        )}
                                        style={{ width: `${audioProgress}%` }}
                                    />

                                    {/* Progress indicator */}
                                    <div
                                        className={cn(
                                            "absolute top-0 w-1 h-full",
                                            isCurrentUser ? "bg-primary-foreground" : "bg-primary",
                                        )}
                                        style={{
                                            left: `calc(${audioProgress}% - 2px)`,
                                            display: audioProgress > 0 ? "block" : "none",
                                        }}
                                    />
                                </div>
                                <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                                    <span>{formatTime(currentTime)}</span>
                                    <span>
                                        {audioDuration ? formatTime(audioDuration) : "--:--"}
                                    </span>
                                </div>
                                <audio
                                    ref={audioRef}
                                    src={CHAT_MEDIUM + content}
                                    onEnded={handleAudioEnded}
                                    onTimeUpdate={handleAudioTimeUpdate}
                                    onLoadedMetadata={handleAudioLoaded}
                                    className="hidden"
                                />
                            </div>
                        </div>
                        <div className="ml-12 text-xs text-muted-foreground">ຂໍ້ຄວາມສຽງ</div>
                    </div>
                );

            case "FILE":
                const fileName = content.split("/").pop() || "file";
                return (
                    <Card className="flex gap-3 items-center p-3">
                        <FileText size={24} />
                        <div className="overflow-hidden flex-1">
                            <p className="text-sm font-medium truncate">{fileName}</p>
                        </div>
                        <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => window.open(CHAT_FILE + content, "_blank")}
                        >
                            <Download size={18} />
                        </Button>
                    </Card>
                );

            case "LOCATION":
                try {
                    const locationData = location;
                    if (!locationData) return <p>Location data not available</p>;

                    const { lng, lat } = locationData;
                    const mapUrl = `https://www.google.com/maps?q=${lat},${lng}`;

                    return (
                        <div className="">
                            <Card className="overflow-hidden">
                                <div
                                    className="h-[150px] bg-muted flex items-center justify-center cursor-pointer relative"
                                    onClick={() => window.open(mapUrl, "_blank")}
                                >
                                    {/* Simple map placeholder with pin */}
                                    <div className="absolute inset-0 bg-slate-200">
                                        <div className="flex justify-center items-center w-full h-full">
                                            <MapPin size={32} className="text-primary" />
                                        </div>
                                    </div>
                                    <div className="absolute right-2 bottom-2 px-2 py-1 text-xs rounded bg-white/80">
                                        Click to open in Maps
                                    </div>
                                </div>
                                <div className="flex gap-2 items-center p-3">
                                    <MapPin size={18} />
                                    <p className="text-sm truncate">
                                        {lat.toFixed(6)}, {lng.toFixed(6)}
                                    </p>
                                </div>
                            </Card>
                        </div>
                    );
                } catch (error) {
                    return <p>Invalid location data</p>;
                }

            default:
                return content ? <div>{content}</div> : null;
        }
    };

    if (isDeleted) {
        return (
            <Badge variant={"outline"} className="text-[14px] text-muted-foreground">
                <CircleSlash2 className="mr-3 w-4 h-4" /> ຂໍ້ຄວາມນີ້ຖືກລົບ
            </Badge>
        );
    }

    return (
        <div className="w-full">
            <div
                className={cn(
                    "p-3 rounded-lg",
                    isCurrentUser ? "bg-primary text-primary-foreground" : "bg-muted",
                )}
            >
                {showSender && senderName && !isCurrentUser && (
                    <p className="mb-1 text-xs font-medium">{senderName}</p>
                )}
                {renderAttachments()}
                {renderContent()}
                {timestamp && (
                    <small className="block mt-1 text-xs opacity-70">{timestamp}</small>
                )}
            </div>

            {isClickReceive && !clickReceivedBy?.includes(user?.data?.userChatId) ? <Button onClick={() => acceptDocumentMutation.mutate()} className="mt-3 text-yellow-500" variant={"outline"}>
                {acceptDocumentMutation.isPending ? <Spinner /> : <>  ຮັບເອກະສານ</>}
            </Button> : isClickReceive && clickReceivedBy?.includes(user?.data?.userChatId) ? <Badge>
                ຮັບເອກະສານແລ້ວ
            </Badge> : null}

            {/* Image Preview Dialog */}
            <Dialog
                open={!!previewImage}
                onOpenChange={(open) => !open && setPreviewImage(null)}
            >
                <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 overflow-hidden bg-background/95 backdrop-blur-sm">
                    <div className="flex relative justify-center items-center w-full h-full">
                        <div className="flex absolute top-2 right-2 z-10 gap-2">
                            {/* Download button */}
                            {/* <a target='_blank' rel='noreferrer' href={previewImage as string} download={previewImage}>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="w-8 h-8 rounded-full bg-background/80"

                                >
                                    <Download className="w-4 h-4 text-green-500" />
                                </Button>
                            </a> */}

                            {/* Close button */}
                            <DialogClose className="inline-flex">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="w-8 h-8 rounded-full bg-background/80"
                                >
                                    <X className="w-4 h-4 text-red-500" />
                                </Button>
                            </DialogClose>
                        </div>
                        <img
                            src={previewImage || ""}
                            alt="Preview"
                            className="max-w-full max-h-[85vh] object-contain"
                        />
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default RenderMessage;
